import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Menu, MenuItem as MuiMenuItem, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import ImageIcon from '@/assets/images/Image.svg'
import CloudDownloadIcon from '@/assets/images/cloud-download-alt.svg'
import FilePDFIcon from '@/assets/images/file-pdf.svg'
import { useBreakpoints } from '@/hooks/useBreakpoints'

import Button from './Button'

export interface MenuOption {
  id: string | number
  label: string
  icon: string
  onClick: () => void
}

interface MenuItemProps {
  menuOption: MenuOption
  onCloseMenu: () => void
}

const MenuItem: React.FC<MenuItemProps> = ({ menuOption, onCloseMenu }) => {
  const theme = useTheme()

  return (
    <MuiMenuItem
      sx={{
        width: '100%',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        py: '6px',
        px: '16px',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }}
      onClick={() => {
        menuOption?.onClick()
        onCloseMenu()
      }}
    >
      <img
        src={menuOption.icon}
        alt={menuOption.label}
        style={{ width: '24px', height: '24px' }}
      />
      {menuOption.label}
    </MuiMenuItem>
  )
}

interface SaveRecipeContainerProps {
  disabled?: boolean
  onSaveAsImage?: () => void
  onSaveAsPDF?: () => void
  handleSaveToProject?: () => void
}

const SaveRecipeContainer: React.FC<SaveRecipeContainerProps> = ({
  disabled,
  onSaveAsImage,
  onSaveAsPDF,
  handleSaveToProject,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'buttons' })
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { isDisplay1280 } = useBreakpoints()

  const handleMenuToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const onCloseMenu = () => setAnchorEl(null)
  const noop = () => {}

  const menuOptions: MenuOption[] = [
    {
      id: 1,
      label: t('saveAsImage'),
      icon: ImageIcon,
      onClick: onSaveAsImage || noop,
    },
    {
      id: 2,
      label: t('saveAsPdf'),
      icon: FilePDFIcon,
      onClick: onSaveAsPDF,
    },
    {
      id: 3,
      label: t('saveToProject'),
      icon: CloudDownloadIcon,
      onClick: handleSaveToProject,
    },
  ]

  return (
    <>
      <Button
        aria-controls="save-recipe"
        aria-haspopup="true"
        onClick={handleMenuToggle}
        endIcon={<ExpandMoreIcon />}
        label={isDisplay1280 ? t('saveRecipe') : t('save')}
        color="default"
        variant="text"
        size="small"
        disabled={!disabled}
        sx={{
          backgroundColor: !disabled ? '#FFFFFF4D' : '#90CAF9',
          color: '#000',
          padding: '4px 10px',
          fontSize: '13px',
          fontWeight: '500 !important',
          lineHeight: '22px',
          letterSpacing: '0.46px',
          textTransform: 'uppercase',
          '&:hover': {
            backgroundColor: '#90CAF9',
            opacity: 0.8,
          },
          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.3)',
            backgroundColor: '#FFFFFF4D',
          },
        }}
      />

      <Menu
        id="save-recipe"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseMenu}
        sx={{
          '& .MuiPaper-root': {
            minWidth: '177px',
            maxHeight: '28.125rem',
            borderRadius: '4px',
            backgroundColor: '#292929',
            boxShadow:
              '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {menuOptions.map((menuOption) => (
          <Box key={menuOption.id}>
            <MenuItem menuOption={menuOption} onCloseMenu={onCloseMenu} />
          </Box>
        ))}
      </Menu>
    </>
  )
}

export default SaveRecipeContainer
